import { Button } from "@progress/kendo-react-buttons";
import {
  Window,
  WindowActionsBar,
  ActionsLayout,
} from "@progress/kendo-react-dialogs";
import { FormRenderProps } from "@progress/kendo-react-form";
import isEmpty from "lodash-es/isEmpty";
import React, { PropsWithChildren, useCallback } from "react";
import { Else, If, Then } from "react-if";
import { DisabledContainer } from "@/components/Container";
import ToolTip from "@/components/Kendo/ToolTip";
import { CircularProgress } from "@/components/Kendo/Wrappers";
import { OverlayLoading } from "@/components/Loading";
import Message from "@/components/Message";
import useWindowSize from "@/hooks/useWindowSize";

import "./styles.scss";

export interface Props {
  title?: string;
  description?: React.ReactNode;
  onClose?: () => void;
  onSubmit?: () => void;
  onDirtyChange?: () => void;
  isDirty?: boolean;
  submitText?: string;
  cancelText?: string;
  isLoading?: boolean;
  isSubmitting?: boolean;
  formRenderProps?: FormRenderProps;
  show?: boolean;
  submitDisabled?: boolean;
  isFreemium?: boolean;
  disabledTitle?: string;
  message?: string;
}

export interface WindowProps extends Props, PropsWithChildren {
  width?: number;
  height?: number;
  withInPercentage?: number;
  actionButtons?: boolean | React.ReactNode[];
  actionsBarLayout?: ActionsLayout;
  actionBar?: boolean;
  className?: string;
}

export const percentage = (num: number, per: number): number =>
  (num / 100) * per;

const CustomTitleBar: React.FC<{
  title?: string;
  description?: React.ReactNode;
}> = ({ title, description }) => {
  return (
    <div className="panel-custom--title">
      {title}
      <div className="panel-custom--title-description">{description}</div>
    </div>
  );
};

const SimpleActionBar: React.FC<PropsWithChildren> = ({ children }) => (
  <div
    style={{
      display: "flex",
      alignSelf: "flex-end",
      justifyContent: "end",
    }}
  >
    {children}
  </div>
);

const Panel: React.FC<WindowProps> = props => {
  const [visible, setVisible] = React.useState<boolean>(true);
  const {
    title,
    description,
    children,
    width: _width,
    height: _height,
    formRenderProps,
    submitText = "Save",
    cancelText = "Cancel",
    isLoading = false,
    withInPercentage = 70,
    isDirty,
    onDirtyChange,
    isSubmitting = false,
    actionButtons = [],
    actionsBarLayout = "end",
    submitDisabled,
    isFreemium,
    disabledTitle,
    actionBar = true,
    className = "",
    message,
    onClose: onCloseProp,
  } = props;

  const onClose = useCallback(() => {
    if (isDirty && onDirtyChange) {
      onDirtyChange();
      return;
    }
    setVisible(false);
    onCloseProp?.();
  }, [isDirty, onDirtyChange, onCloseProp]);

  const { height, width } = useWindowSize();
  if (!visible) return null;
  const initialWidth = _width ?? width - 100;
  const initialHeight = _height ?? height;

  const ActionBar = actionBar ? WindowActionsBar : SimpleActionBar;

  return (
    <>
      <Window
        title={<CustomTitleBar title={title} description={description} />}
        className={`window-panel ${className}`}
        draggable={false}
        resizable={false}
        onClose={onClose}
        top={50}
        // initialLeft={100}
        doubleClickStageChange={false}
        height={initialHeight - 100}
        width={percentage(initialWidth, withInPercentage)}
        modal={true}
        left={
          withInPercentage == 100
            ? 50
            : (initialWidth - percentage(initialWidth, withInPercentage)) / 2
        }
      >
        <DisabledContainer show={isLoading} height={`${initialHeight - 100}px`}>
          <OverlayLoading loading={isLoading} backgroundColor={false} />
          {children}
        </DisabledContainer>
        {formRenderProps && (
          <ActionBar layout={actionsBarLayout}>
            <div className="panel-message">
              <If condition={!isEmpty(message)}>
                <Then>
                  <Message text={message} type="alert" />
                </Then>
              </If>
            </div>
            <If
              condition={
                actionButtons !== false &&
                (actionButtons as React.ReactNode[]).length === 0
              }
            >
              <Then>
                <Button
                  size="medium"
                  onClick={onClose}
                  disabled={isSubmitting}
                  style={{
                    padding: "var(--tlx-space-1) 1.5625rem",
                    margin: "var(--tlx-space-2) 0px",
                    height: 40,
                    boxShadow: "none",
                    borderRadius: 4,
                  }}
                >
                  {cancelText}
                </Button>
                <ToolTip
                  anchorElement="target"
                  title={submitDisabled ? disabledTitle : ""}
                  isFreemium={submitDisabled ? isFreemium : undefined}
                >
                  <Button
                    size="medium"
                    onClick={formRenderProps.onSubmit}
                    themeColor={"secondary"}
                    id="panel-submit-button"
                    disabled={
                      (submitDisabled ?? !formRenderProps.modified) ||
                      isSubmitting ||
                      !formRenderProps.valid
                    }
                    style={{
                      padding: "var(--tlx-space-1) 1.5625rem",
                      margin: 10,
                      height: 40,
                      boxShadow: "none",
                      borderRadius: 4,
                    }}
                  >
                    {isSubmitting && (
                      <CircularProgress
                        style={{ color: "white", verticalAlign: "bottom" }}
                        size={17}
                      />
                    )}{" "}
                    {submitText}
                  </Button>
                </ToolTip>
              </Then>
              <Else>
                {(actionButtons as React.ReactNode[]).length && actionButtons}
              </Else>
            </If>
          </ActionBar>
        )}
      </Window>
    </>
  );
};

export default Panel;
